/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import { debounce } from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  type ContactIdentity,
  type DuplicateMatch,
  findContactsByName,
  lookupDuplicate,
} from 'app/lib/contactDuplicates'
import type { ContactJson } from 'app/types/entities'
import { bg } from 'lib/background'
import { MIN_PHONE_DIGITS } from 'lib/contactSearch'
import { digitsOnly } from 'lib/phone'

const MAX_SUGGESTIONS = 5
const LOOKUP_DEBOUNCE_MS = 250

type Typed = {
  name: string
  cellPhone: string
  email: string
  locationId: string
}

/**
 * The two lookups behind the quick-create form, fed by what has been typed so far.
 *
 * Suggestions ("Already a contact?") follow the heading, or the phone digits once
 * a number has moved down or was prefilled. The exact duplicate check only runs
 * once a complete phone or a plausible email exists, and it wins over suggestions
 * when it finds something. Dismissing an advisory match keeps it out of both.
 */
export function useQuickContactLookup({ name, cellPhone, email, locationId }: Typed) {
  const [suggestions, setSuggestions] = useState<ContactJson[]>([])
  const [duplicate, setDuplicate] = useState<DuplicateMatch>()
  const [dismissedIds, setDismissedIds] = useState<string[]>([])
  const dismissedIdsRef = useRef(dismissedIds)
  dismissedIdsRef.current = dismissedIds

  const phoneDigitsTyped = digitsOnly(cellPhone ?? ``)

  const identity = useMemo<ContactIdentity>(() => {
    const trimmed = (email ?? ``).trim()
    return {
      cellPhone: phoneDigitsTyped.length === 10 ? phoneDigitsTyped : undefined,
      email: trimmed.includes(`@`) ? trimmed : undefined,
      locationId: locationId || undefined,
    }
  }, [phoneDigitsTyped, email, locationId])

  const findDuplicate = useMemo(
    () =>
      debounce((currentName: string, currentIdentity: ContactIdentity) => {
        bg(async () => {
          setDuplicate(await lookupDuplicate(currentName, currentIdentity, dismissedIdsRef.current))
        })
      }, LOOKUP_DEBOUNCE_MS),
    [],
  )
  useEffect(() => {
    findDuplicate(name, identity)
  }, [name, identity, findDuplicate])

  const suggestionQuery = name.trim() || (phoneDigitsTyped.length >= MIN_PHONE_DIGITS ? phoneDigitsTyped : ``)
  const findSuggestions = useMemo(
    () =>
      debounce((typed: string) => {
        bg(async () => {
          setSuggestions(await findContactsByName(typed, MAX_SUGGESTIONS))
        })
      }, LOOKUP_DEBOUNCE_MS),
    [],
  )
  useEffect(() => {
    findSuggestions(suggestionQuery)
  }, [suggestionQuery, findSuggestions])

  /**
   * The submit-time check. Re-run synchronously rather than trusting the debounced
   * result: typing fast and hitting Enter otherwise races past the lookup and
   * creates the duplicate anyway. A blocking match is surfaced so Save disables.
   */
  const recheckDuplicate = useCallback(async (currentName: string, currentIdentity: ContactIdentity) => {
    const match = await lookupDuplicate(currentName, currentIdentity, dismissedIdsRef.current)
    if (match?.blocking) setDuplicate(match)
    return match
  }, [])

  const dismiss = useCallback((contact: ContactJson) => {
    setDismissedIds((ids) => (contact.id ? [...ids, contact.id] : ids))
    setDuplicate(undefined)
  }, [])

  const visibleSuggestions = duplicate ? [] : suggestions.filter((s) => !dismissedIds.includes(s.id ?? ``))

  return { suggestions: visibleSuggestions, duplicate, dismiss, recheckDuplicate }
}
